import * as z from "zod";
import { skipConditionSchema } from "../advanced-flow/advanced-flow-db-parser";
import { ConjunctionMode } from "../segments/segment-types";
import { ShopperAgentAutomationConfigSchema } from "./automation-config";
import { ShopperAgentCooldownConfigSchema } from "./cooldown-config";
import { ShopperAgentDiscountConfigSchema } from "./discount-config";
import { ShopperAgentIMessageConfigSchema } from "./imessage-config";
import { ShopperAgentReplyConfigSchema } from "./reply-config";
import { SkillConfigSchema } from "./skill-config";

/**
 * Skip conditions for a shopper-agent trigger. Mirrors the shape used by
 * advanced-flow trigger steps so the same UI builders and runtime evaluator
 * can be reused. When any condition evaluates true (OR semantics), the
 * shopper agent does NOT receive the trigger signal — the system falls
 * through to standard advanced-flow processing instead.
 *
 * `conjunctionMode` is fixed to OR today; the field is kept for forward
 * compatibility with the advanced-flow schema.
 *
 * `actionTypes` on each condition is inherited from advanced-flow
 * SkipCondition for schema parity but is ignored at the shopper-agent layer
 * (there are no action-step types to gate at this level).
 */
export const ShopperAgentTriggerSkipConditionsSchema = z.object({
  conjunctionMode: z.literal(ConjunctionMode.OR),
  conditions: z.array(skipConditionSchema),
});
export type ShopperAgentTriggerSkipConditions = z.infer<
  typeof ShopperAgentTriggerSkipConditionsSchema
>;

export const TriggerProcessingPolicyConfigSchema = z.object({
  buffer: z.object({ bypass: z.boolean().optional() }).optional(),
  sentinel: z.object({ bypass: z.boolean().optional() }).optional(),
});
export type TriggerProcessingPolicyConfig = z.infer<
  typeof TriggerProcessingPolicyConfigSchema
>;

/**
 * Per-trigger configuration. Overrides top-level ShopperAgentSettings
 * for a specific trigger. Only defined fields override; undefined fields
 * inherit from the top level.
 */
export const TriggerConfigSchema = z.object({
  enabled: z.boolean().optional(),
  iMessageConfig: ShopperAgentIMessageConfigSchema.optional(),
  replyConfig: ShopperAgentReplyConfigSchema.optional(),
  discountConfig: ShopperAgentDiscountConfigSchema.optional(),
  cooldownConfig: ShopperAgentCooldownConfigSchema.optional(),
  /**
   * Advanced flows the merchant has attached to this trigger via the playbook.
   * Selected automations run alongside the shopper agent even when the agent
   * would normally suppress the standard advanced-flow dispatch. Absent or
   * empty means no allow-listed flows — standard suppression applies.
   */
  automations: z.array(ShopperAgentAutomationConfigSchema).optional(),
  skills: z.record(z.string(), SkillConfigSchema).optional(),
  /**
   * Merchant-authored override for the trigger's `guidance` prompt.
   * When unset, the vanguard falls back to the trigger-definition default.
   */
  guidance: z.string().optional(),
  /**
   * Optional skip conditions evaluated against the trigger's payload
   * (`SchemaInstance`). If any condition matches (OR semantics), the trigger
   * is NOT routed to the marketing/shopper agent. Reuses the advanced-flow
   * `SkipCondition` schema so the same UI builders and runtime evaluator
   * apply across both surfaces.
   */
  skipConditions: ShopperAgentTriggerSkipConditionsSchema.optional(),
  /**
   * Per-trigger coordinator behavior overrides. Runtime only honors these when
   * the trigger definition's processingPolicy allows the corresponding override.
   */
  processingPolicy: TriggerProcessingPolicyConfigSchema.optional(),
});

export type TriggerConfig = z.infer<typeof TriggerConfigSchema>;
